import React, { useState } from "react";
import { Grid, DialogActions, Button, CircularProgress, Typography } from "@mui/material";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useDialog } from "../../../../contexts/DialogContext";
import useUser from "../../../../hooks/useUser";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CustomTextField from "../../../../components/CustomTextField";
import { useTranslation } from "react-i18next";

const validationSchema = Yup.object().shape({
  expiryDate: Yup.string().required("Required"),
  comments: Yup.string().required("Required"),
});

const BulkChangeValidity = (props) => {
  const { selectedRows, refreshPage } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { changeValidity } = useUser();
  const { t } = useTranslation();
  const { unSetDialog } = useDialog();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      expiryDate: "",
      comments: "Bulk validity change",
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = methods;

  const onSubmit = async (data) => {
    if (!selectedRows || selectedRows.length === 0) {
      dispatch(showErrorSnackbar("Please select at least one user"));
      return;
    }

    setIsSubmitting(true);
    try {
      for (const radUserId of selectedRows) {
        const payload = {
          radUserId,
          expiryDate: data.expiryDate,
          comments: data.comments,
        };
        await changeValidity(payload);
      }
      dispatch(
        showSuccessSnackbar(`Validity updated for ${selectedRows.length} users successfully`),
      );
      unSetDialog("bulk_change_validity");
      if (refreshPage) refreshPage();
    } catch (ex) {
      dispatch(showErrorSnackbar(ex.message || "Failed to update bulk validity"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={12} className="row" pt={8}>
            <Grid item md={6}>
              <CustomDatePicker
                name="expiryDate"
                label={t("change_validity")}
                range={false}
                control={control}
                fields={validationSchema.fields}
                timePicker={true}
                format="DD-MM-YYYY HH:mm:ss A"
                hideSeconds={false}
                conversionFormat="YYYY-MM-DD HH:mm:ss A"
              />
            </Grid>
            <Grid item xs={9.5} sm={12} md={6} lg={6} xl={6}>
              <CustomTextField
                label={t("comments")}
                name="comments"
                type="text"
                my={2}
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
                fields={validationSchema.fields}
              />
            </Grid>
          </Grid>
          <DialogActions>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<ScheduleIcon />}
              endIcon={isSubmitting ? <CircularProgress size={24} /> : null}
              disabled={isSubmitting}
            >
              Change
            </Button>

            <Button
              variant="text"
              onClick={() => unSetDialog("bulk_change_validity")}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};

export default BulkChangeValidity;
